import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';

/**
 * Opens the Support Machine widget (Lexi).
 *
 * Two ways in, tried in order:
 *
 * 1. `window.OsmosSupport.open()` — a real API, added in osmos-pm#342. This is
 *    the supported path and the one to keep.
 * 2. Clicking the widget's own launcher, `.osmos-support-fab`, which the widget
 *    binds to its internal openPanel handler. Verified against the live script
 *    on 2026-09-05, and end-to-end in a browser.
 *
 * The fallback exists only because the app and the widget deploy separately —
 * this must keep working against a widget that predates the API, including a
 * cached one. It depends on an internal class name, so once osmos-pm#342 has
 * been live long enough that no cached widget lacks the API, DELETE the
 * fallback and the coupling with it.
 *
 * WHY THIS IS ASYNC. index.html loads support-widget.js with `async` from
 * another origin, while the entry points that call this — the trial banner's
 * "Talk with Sales", "Live Chat" in the help menu — are on screen from the
 * first paint. A person can therefore click one before the widget has
 * arrived. A synchronous call would find nothing and do nothing, with no
 * explanation. So the first attempt is immediate, and if the widget is not
 * there yet the call waits for it rather than failing.
 */
@Injectable({ providedIn: 'root' })
export class SupportWidgetService {
  private static readonly LAUNCHER_SELECTOR = '.osmos-support-fab';

  /** How long to keep waiting for an async script that may never arrive. */
  private static readonly MAX_WAIT_MS = 5000;
  private static readonly POLL_INTERVAL_MS = 150;

  /**
   * The wait currently in progress, if any. A person can click a chat entry
   * more than once while an async widget is still loading; without this each
   * click would start its own polling timer.
   */
  private pendingOpen: Promise<boolean> | null = null;

  constructor(private readonly logger: LoggerService) {}

  /**
   * @returns a promise for whether the widget was reached. It resolves true as
   * soon as the panel is opened — usually on the first synchronous attempt —
   * and false only after MAX_WAIT_MS with no widget, which is the one case a
   * caller should tell the person about.
   */
  open(): Promise<boolean> {
    if (this.tryOpen()) {
      return Promise.resolve(true);
    }

    // Already waiting from an earlier click — join that wait rather than
    // starting a second timer.
    if (this.pendingOpen) {
      return this.pendingOpen;
    }

    this.pendingOpen = new Promise<boolean>((resolve) => {
      const deadline = Date.now() + SupportWidgetService.MAX_WAIT_MS;
      const timer = setInterval(() => {
        if (this.tryOpen()) {
          clearInterval(timer);
          this.pendingOpen = null;
          resolve(true);
          return;
        }
        if (Date.now() >= deadline) {
          clearInterval(timer);
          this.pendingOpen = null;
          this.logger.warn(
            `Support widget unreachable after ${SupportWidgetService.MAX_WAIT_MS}ms: no window.OsmosSupport.open and no ${SupportWidgetService.LAUNCHER_SELECTOR}. Is support-widget.js loaded?`,
          );
          resolve(false);
        }
      }, SupportWidgetService.POLL_INTERVAL_MS);
    });

    return this.pendingOpen;
  }

  /** One attempt, no waiting. */
  private tryOpen(): boolean {
    const api = (window as unknown as { OsmosSupport?: { open?: () => unknown } }).OsmosSupport;
    if (typeof api?.open === 'function') {
      api.open();
      return true;
    }

    // Widget predates osmos-pm#342. See the note on this class.
    const launcher = document.querySelector<HTMLElement>(SupportWidgetService.LAUNCHER_SELECTOR);
    if (launcher) {
      launcher.click();
      return true;
    }

    return false;
  }
}
